import { Controller, useForm } from 'react-hook-form'
import { useEffect } from 'react'
import {
  RadioGroup,
  TextField,
  Button,
  ButtonColor,
  NotificationType,
} from '../../../components'
import {
  videoPlacementsOptions,
  getVideoPlacementFormValue,
  getVideoPlacementOtherValue,
} from '../../../utils/mappers'
import { BrandOnboardingStep, Brand, Campaign } from '../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import { useNotification } from '../../../utils/hooks'
import { useMutation } from '@apollo/client'
import { UPDATE_BRAND, UPDATE_CAMPAIGN } from '../../../gql'
import { Steps } from '../components/Steps'
import { Bullets } from '../components/Bullets'

type SetVideoPlacementsFormData = {
  videoPlacements?: string | null
  videoPlacementsOther?: string | null
}

interface Props {
  brand: Brand
  campaign: Campaign
}

const SetVideoPlacements = ({ brand, campaign }: Props) => {
  const { addNotification } = useNotification()

  const [updateBrand, { loading: loadingUpdateBrand }] =
    useMutation(UPDATE_BRAND)
  const [updateCampaign, { loading: loadingUpdateCampaign }] =
    useMutation(UPDATE_CAMPAIGN)

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
    watch,
    trigger,
  } = useForm<SetVideoPlacementsFormData>({
    defaultValues: {
      videoPlacements: getVideoPlacementFormValue(campaign?.videoPlacements),
      videoPlacementsOther: getVideoPlacementOtherValue(
        campaign?.videoPlacements,
      ),
    },
  })

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewBrandsOnboardingSetVideoPlacements)
  }, [])

  const selectedPlacement = watch('videoPlacements')
  const selectedOtherValue = watch('videoPlacementsOther')

  // Trigger validation for text input when "other" is selected
  useEffect(() => {
    if (selectedPlacement === 'other') {
      trigger('videoPlacementsOther')
    }
  }, [selectedPlacement, trigger])

  // Trigger validation when text input value changes (works for both filled and empty)
  useEffect(() => {
    if (selectedPlacement === 'other') {
      trigger('videoPlacementsOther')
    }
  }, [selectedOtherValue, selectedPlacement, trigger])

  const goBack = async () => {
    try {
      await updateBrand({
        variables: {
          id: brand.id,
          input: {
            onboarding: BrandOnboardingStep.setVideoAspectRatio,
          },
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  const onSubmit = async (data: SetVideoPlacementsFormData) => {
    try {
      // Map videoPlacements value similar to NewCampaign logic
      const videoPlacementsValue =
        data.videoPlacements === 'other'
          ? data.videoPlacementsOther
          : data.videoPlacements

      await Promise.all([
        updateCampaign({
          variables: {
            id: campaign.id,
            input: {
              videoPlacements: videoPlacementsValue,
            },
          },
        }),
        updateBrand({
          variables: {
            id: brand.id,
            input: {
              onboarding: BrandOnboardingStep.setRequirements,
            },
          },
        }),
      ])
    } catch (error) {
      console.log('@error SetVideoPlacements', error.message)
      if (error.message) {
        setError('videoPlacements', { type: 'custom', message: error.message })
        return
      }
    }
  }

  return (
    <div className='min-h-screen flex flex-col pb-14'>
      <div className='flex-grow'>
        <Steps
          brand={brand}
          stepCurrent={BrandOnboardingStep.setVideoPlacements}
        />

        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='md:shadow rounded-t-md sm:overflow-hidden'>
                <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                  <div className='sm:mx-auto'>
                    <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
                      Where will you use these videos?
                    </h2>
                    <p className='mt-2 md:text-center text-sm text-gray-600'>
                      Tell us where you'll use these videos so creators can
                      match the style to each channel for native,
                      better-performing content.
                    </p>
                  </div>

                  <div className='grid gap-6'>
                    <Controller
                      name='videoPlacements'
                      control={control}
                      rules={{
                        required:
                          'Please select where you will use these videos.',
                      }}
                      render={({ field }) => (
                        <RadioGroup
                          options={videoPlacementsOptions}
                          className='grid grid-cols-2'
                          field={field}
                          error={errors?.videoPlacements?.message}
                        />
                      )}
                    />

                    {selectedPlacement === 'other' && (
                      <div className='mt-4'>
                        <TextField
                          label='Please specify'
                          placeholder='Where will you use these videos?'
                          error={errors?.videoPlacementsOther?.message}
                          {...register('videoPlacementsOther', {
                            required:
                              selectedPlacement === 'other'
                                ? 'Please enter where you will use these videos.'
                                : false,
                            maxLength: {
                              value: 100,
                              message: 'Please use maximum 100 characters.',
                            },
                          })}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 sm:px-6'>
                <Button
                  title='Go back'
                  type='button'
                  colour={ButtonColor.white}
                  loading={loadingUpdateBrand}
                  disabled={loadingUpdateBrand}
                  onClick={goBack}
                />
                <Button
                  title='Continue'
                  type='submit'
                  loading={loadingUpdateBrand || loadingUpdateCampaign}
                  disabled={loadingUpdateBrand || loadingUpdateCampaign}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={BrandOnboardingStep.setVideoPlacements} />
    </div>
  )
}

export { SetVideoPlacements }
